import { createFileRoute } from "@tanstack/react-router";
import { Suspense, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { optionalString } from "@/lib/search";
import { signupHead } from "@/components/marketing/site/signup/signup-head";
import { readAttribution } from "@/lib/marketing/attribution";
import { Reveal } from "@/components/marketing/reveal";
import { MarketingShell } from "@/components/marketing/site/marketing-shell";
import { HoneypotField } from "@/components/marketing/honeypot-field";
import { SignupHero } from "@/components/marketing/site/signup/signup-hero";
import { CommunityCount } from "@/components/marketing/site/signup/community-count";
import { ProductsStage } from "@/components/marketing/site/signup/products-stage";
import { OrderSummary, type SummaryAction } from "@/components/marketing/site/signup/order-summary";
import { OrderReview } from "@/components/marketing/site/signup/order-review";
import { CheckoutStage } from "@/components/marketing/site/signup/checkout-stage";
import { type SignupDetails } from "@/components/marketing/site/signup/signup-details";
import { SignupSuccess } from "@/components/marketing/site/signup/signup-success";
import { SignupStep } from "@/components/marketing/site/signup/signup-step";
import { SignupMobileBar } from "@/components/marketing/site/signup/signup-mobile-bar";
import { StageNav } from "@/components/marketing/site/signup/stage-nav";
import {
  SIGNUP_STAGES,
  StepRail,
  type SignupStage,
} from "@/components/marketing/site/signup/step-rail";
import {
  orderKey,
  useSignupCheckout,
} from "@/components/marketing/site/signup/use-signup-checkout";
import { useProductSelection } from "@/components/marketing/site/signup/use-product-selection";
import { useSavedDraft } from "@/components/marketing/site/signup/use-saved-draft";
import { STAGE_COPY } from "@/components/marketing/site/signup/stage-copy";
import { BundleCelebration } from "@/components/marketing/site/signup/lazy-bundle-celebration";
import {
  EMPTY_DETAILS,
  railSteps,
  validateDraft,
  type SignupErrors,
} from "@/components/marketing/site/signup/signup-state";
import {
  missingTerms,
  planPrice,
  planRefusal,
  quoteSubscription,
  type BillingPeriod,
  type SubscriptionCoupon,
  type SubscriptionProductKey,
  type SubscriptionTermKey,
} from "@alm/domain/marketing";

const FORM_ID = "signup-form";

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

/** Four stages (communities, products, cart, checkout) on one draft. The order is validated with
 * the server's own schema; the server prices it again at Stripe checkout and when it files the
 * order from what Stripe holds. */
function MarketingSignup() {
  const search = Route.useSearch();
  const [stage, setStage] = useState<SignupStage>("communities");
  const [reviewed, setReviewed] = useState(false);
  const [communities, setCommunities] = useState(1);
  const [billing, setBilling] = useState<BillingPeriod>("monthly");
  const [details, setDetails] = useState<SignupDetails>(EMPTY_DETAILS);
  const [accepted, setAccepted] = useState<Set<SubscriptionTermKey>>(() => new Set());
  const [coupons, setCoupons] = useState<SubscriptionCoupon[]>([]);
  const [errors, setErrors] = useState<SignupErrors>({});
  const [__hp, setHp] = useState(""); // honeypot (intentionally non-human field name)
  const [source] = useState(() => readAttribution("signup"));
  const selection = useProductSelection(() =>
    setErrors((prev) => {
      if (!prev.products) return prev;
      const next = { ...prev };
      delete next.products;
      return next;
    }),
  );
  const { products, toggle: toggleProduct } = selection;
  const order = useSignupCheckout(
    orderKey({
      communities,
      products: [...products],
      billing,
      coupons: coupons.map((c) => c.code),
    }),
  );
  const { payment, placed } = order;
  const hadCode = coupons.length > 0;
  useSavedDraft({
    draft: { stage, reviewed, communities, billing, products: [...products], details, hadCode },
    done: placed !== null || payment.kind === "completing" || payment.kind === "unconfirmed",
    returningFromStripe: Boolean(search.checkout),
    onRestore: (saved) => {
      setStage(saved.stage);
      setReviewed(saved.reviewed);
      setCommunities(saved.communities);
      setBilling(saved.billing);
      setDetails(saved.details);
      selection.restore(saved.products);
    },
  });

  const quote = useMemo(
    () => quoteSubscription({ communities, products: [...products], coupons, billing }),
    [communities, products, coupons, billing],
  );
  const unitPriceOf = (key: SubscriptionProductKey) => planPrice(key, quote.coupons, billing);
  const steps = railSteps(quote, details, accepted, reviewed);

  const goTo = (next: SignupStage) => {
    if (next === "review") setReviewed(true);
    setStage(next);
    scrollToTop();
  };
  const back = () => {
    const i = SIGNUP_STAGES.indexOf(stage);
    if (i > 0) goTo(SIGNUP_STAGES[i - 1] ?? "communities");
  };

  /* Back from a payment method that needed a redirect: the session id is in
     the address, and the order is filed from what Stripe holds. Once. */
  const returned = useRef(false);
  useEffect(() => {
    if (!search.checkout || returned.current) return;
    returned.current = true;
    setStage("checkout");
    order.completeCheckout(search.checkout);
    window.history.replaceState(null, "", window.location.pathname);
  }, [search.checkout, order]);

  const checkedDraft = () =>
    validateDraft({ details, communities, products, billing, accepted, coupons, source, __hp });

  const toPayment = (e: FormEvent) => {
    e.preventDefault();
    if (payment.kind !== "idle" && payment.kind !== "unavailable") return;
    const checked = checkedDraft();
    setErrors(checked.errors);
    if (checked.focusId) {
      if (checked.errors.products) setStage("products");
      requestAnimationFrame(() => document.getElementById(checked.focusId ?? "")?.focus());
      return;
    }
    if (checked.input) order.startCheckout(checked.input);
  };
  const placeWithoutCard = () => {
    const checked = checkedDraft();
    setErrors(checked.errors);
    if (checked.input) order.placeWithoutCard(checked.input);
  };

  // A year is never PEOPLE Core alone: the products and review stages hold until it is not.
  const refusal =
    stage === "products" || stage === "review" ? planRefusal([...products], billing) : null;
  const unaccepted = missingTerms([...accepted]).length > 0;
  const continueTo = (next: SignupStage): SummaryAction => ({
    submit: false,
    label: next === "checkout" ? "Continue to checkout" : "Continue",
    pending: false,
    inactive: refusal !== null,
    notice: refusal,
    onClick: () => {
      if (refusal) return;
      if (stage === "products" && quote.lines.length === 0) {
        setErrors((prev) => ({ ...prev, products: "Choose at least one product" }));
        document.getElementById("product-platform")?.focus();
        return;
      }
      goTo(next);
    },
  });
  const busy = payment.kind === "starting" || payment.kind === "completing";
  const paid = payment.kind === "completing" || payment.kind === "unconfirmed";
  const nothingDue = quote.periodTotal === 0;
  // Once Stripe's card form is up, its own button is the only action; ours step aside.
  const action: SummaryAction | null =
    stage === "checkout" && payment.kind === "ready"
      ? null
      : stage === "checkout" && payment.kind === "unconfirmed"
        ? { submit: false, label: "Confirm my order", pending: false, onClick: order.retryConfirm }
        : stage === "communities"
          ? continueTo("products")
          : stage === "products"
            ? continueTo("review")
            : stage === "review"
              ? continueTo("checkout")
              : {
                  submit: true,
                  label: nothingDue ? "Continue to add a card" : "Continue to payment",
                  pending: busy,
                  inactive: unaccepted,
                  notice: errors.terms && unaccepted ? errors.terms : null,
                  pendingLabel:
                    payment.kind === "completing"
                      ? "Confirming your payment…"
                      : "Opening the card form…",
                  formId: FORM_ID,
                };

  const copy = STAGE_COPY[stage];
  const number = SIGNUP_STAGES.indexOf(stage) + 1;

  return (
    <MarketingShell>
      <SignupHero />

      <section className="relative border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-6xl px-4 py-12 pb-28 md:py-16 lg:pb-16">
          {placed ? (
            <SignupSuccess
              quote={placed.quote}
              paid={placed.paid}
              firstName={details.firstName}
              email={details.email}
              orderUrl={placed.orderUrl}
              emailed={placed.emailed}
            />
          ) : (
            <>
              <Reveal>
                <StepRail steps={steps} current={stage} onSelect={goTo} locked={paid} />
              </Reveal>
              <div
                className={`mt-10 grid items-start gap-8 ${
                  stage === "review" ? "" : "lg:grid-cols-[minmax(0,1fr)_22rem]"
                }`}
              >
                <form
                  id={FORM_ID}
                  onSubmit={toPayment}
                  noValidate
                  className="min-w-0 space-y-8"
                  data-testid="form-signup"
                  data-stage={stage}
                >
                  <HoneypotField id="signup-hp" value={__hp} onChange={setHp} />
                  <SignupStep id={stage} number={number} title={copy.title} lead={copy.lead}>
                    {stage === "communities" && (
                      <CommunityCount value={communities} onChange={setCommunities} />
                    )}
                    {stage === "products" && (
                      <ProductsStage
                        products={products}
                        billing={billing}
                        unitPriceOf={unitPriceOf}
                        error={errors.products}
                        onToggle={toggleProduct}
                        onSelectAll={selection.selectAll}
                        onBillingChange={setBilling}
                      />
                    )}
                    {stage === "review" && (
                      <OrderReview
                        quote={quote}
                        onEditCommunities={() => goTo("communities")}
                        onEditProducts={() => goTo("products")}
                        onRemove={(key) => toggleProduct(key, false)}
                        onApplyCoupon={(coupon) =>
                          // One code per product and one for the order: a new one replaces the old.
                          setCoupons((prev) => [
                            ...prev.filter((held) => held.product !== coupon.product),
                            coupon,
                          ])
                        }
                        onRemoveCoupon={(code) =>
                          setCoupons((prev) => prev.filter((c) => c.code !== code))
                        }
                        onBillingChange={setBilling}
                      />
                    )}
                    {stage === "checkout" && (
                      <CheckoutStage
                        details={details}
                        errors={errors}
                        onChange={(patch) => setDetails((prev) => ({ ...prev, ...patch }))}
                        accepted={accepted}
                        onAccept={(key, on) =>
                          setAccepted((prev) => {
                            const next = new Set(prev);
                            if (on) next.add(key);
                            else next.delete(key);
                            return next;
                          })
                        }
                        payment={payment}
                        onEditDetails={order.editDetails}
                        onPaymentComplete={order.completeCheckout}
                        onPlaceWithoutCard={placeWithoutCard}
                        placingWithoutCard={order.placingWithoutCard}
                        billing={billing}
                        due={quote.periodTotal}
                      />
                    )}
                  </SignupStep>
                  <StageNav
                    onBack={stage === "communities" || paid ? undefined : back}
                    action={stage === "review" ? action : null}
                  />
                </form>

                {stage !== "review" && (
                  <div className="lg:sticky lg:top-24" id="summary">
                    <Reveal delay={80}>
                      <OrderSummary
                        quote={quote}
                        action={action}
                        onBillingChange={setBilling}
                        locked={paid}
                      />
                    </Reveal>
                  </div>
                )}
              </div>

              {action && (
                <SignupMobileBar total={quote.periodTotal} billing={billing} action={action} />
              )}
              {selection.celebrating && (
                <Suspense fallback={null}>
                  <BundleCelebration
                    quote={quote}
                    onContinue={() => {
                      selection.endCelebration();
                      goTo("review");
                    }}
                    onClose={selection.endCelebration}
                  />
                </Suspense>
              )}
            </>
          )}
        </div>
      </section>
    </MarketingShell>
  );
}

export const Route = createFileRoute("/v2/subscribe")({
  /* Stripe sends the browser back with ?checkout=… when a payment method needed a redirect. */
  validateSearch: (search: Record<string, unknown>): { checkout?: string } => ({
    checkout: optionalString(search.checkout),
  }),
  component: MarketingSignup,
  head: signupHead,
});
